import * as faceapi from 'face-api.js'

const MAX_SIZE_BYTES = 1024 * 1024
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/bmp']
const FACE_MODEL_PATH = '/models'
let faceModelPromise = null

function loadImage(file) {
   return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)

      img.onload = () => {
         URL.revokeObjectURL(url)
         resolve(img)
      }

      img.onerror = () => {
         URL.revokeObjectURL(url)
         reject(new Error('Unable to read this image. Please upload a clear JPG, PNG, or BMP photo.'))
      }

      img.src = url
   })
}

function loadFaceModel() {
   if (!faceModelPromise) {
      faceModelPromise = faceapi.nets.tinyFaceDetector
         .loadFromUri(FACE_MODEL_PATH)
         .catch((error) => {
            faceModelPromise = null
            throw error
         })
   }

   return faceModelPromise
}

async function detectFaces(img) {
   await loadFaceModel()
   return faceapi.detectAllFaces(
      img,
      new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.35 })
   )
}

function getImageStats(img) {
   const canvas = document.createElement('canvas')
   const size = 64
   canvas.width = size
   canvas.height = size

   const ctx = canvas.getContext('2d')
   ctx.drawImage(img, 0, 0, size, size)

   const data = ctx.getImageData(0, 0, size, size).data
   let skinLike = 0
   let saturated = 0
   let dark = 0
   let light = 0

   for (let i = 0; i < data.length; i += 4) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      const max = Math.max(r, g, b)
      const min = Math.min(r, g, b)

      if (r > 95 && g > 40 && b > 20 && max - min > 15 && r > g && r > b) {
         skinLike++
      }

      if (max - min > 90) {
         saturated++
      }

      if (max < 45) {
         dark++
      }

      if (min > 235) {
         light++
      }
   }

   const total = data.length / 4
   return {
      skinRatio: skinLike / total,
      saturatedRatio: saturated / total,
      darkRatio: dark / total,
      lightRatio: light / total,
   }
}

export async function validateProfessionalPhoto(file) {
   if (!file) {
      return { valid: false, message: 'Professional photo is required.' }
   }

   if (!ACCEPTED_TYPES.includes(file.type)) {
      return { valid: false, message: 'Only JPG, PNG, or BMP professional photo is allowed.' }
   }

   if (file.size > MAX_SIZE_BYTES) {
      return { valid: false, message: 'Photo size must not exceed 1MB.' }
   }

   const img = await loadImage(file)
   const minSide = Math.min(img.naturalWidth, img.naturalHeight)
   const maxSide = Math.max(img.naturalWidth, img.naturalHeight)
   const ratio = maxSide / minSide

   if (minSide < 180) {
      return { valid: false, message: 'Photo is too small. Please upload a clear professional face photo.' }
   }

   if (img.naturalWidth > img.naturalHeight * 1.1 || ratio > 1.65) {
      return { valid: false, message: 'Please upload a single-person portrait professional photo. Group photos, selfies, screenshots, and documents are not accepted.' }
   }

   let faces = []
   try {
      faces = await detectFaces(img)
   } catch (error) {
      return { valid: false, message: 'Unable to verify this photo. Please refresh the page and upload the photo again.' }
   }

   if (faces.length !== 1) {
      return {
         valid: false,
         message: faces.length > 1
            ? 'More than one face was detected. Please upload a professional photo of this member only.'
            : 'No clear face was detected. Please upload a front-facing professional portrait.'
      }
   }

   const faceBox = faces[0].box
   const faceWidthRatio = faceBox.width / img.naturalWidth
   const faceHeightRatio = faceBox.height / img.naturalHeight
   const faceCenterX = (faceBox.x + (faceBox.width / 2)) / img.naturalWidth

   if (faceWidthRatio < 0.22 || faceHeightRatio < 0.22) {
      return { valid: false, message: 'The face is too small. Please upload a close, single-person professional portrait.' }
   }

   if (faceCenterX < 0.2 || faceCenterX > 0.8) {
      return { valid: false, message: 'Please use a properly centred professional portrait.' }
   }

   const stats = getImageStats(img)

   if (stats.darkRatio > 0.55 || stats.lightRatio > 0.75) {
      return { valid: false, message: 'Photo is not clear enough. Please upload a clear face photo.' }
   }

   if (stats.saturatedRatio > 0.55 && stats.skinRatio < 0.03) {
      return { valid: false, message: 'This does not look like a professional face photo.' }
   }

   return { valid: true, message: 'Professional photo accepted.' }
}
